import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxRuntime automatic */
/** @jsxImportSource react */
/**
 * StarScribe beside the chat (docs/CONTRACT-starscribe-clients.md sections 1 and 2):
 *  - the side panel's tab (picture 11, the owner's pick "Preview | StarScribe"), registered through the panel's API
 *    (sidePanel.ts): id 'starscribe', label 'StarScribe', payload `{ pageId }`. Its header extras are the page's header
 *    (title, History, Share, Export) and, in the desktop app, Pop out; its body is the page.
 *  - the narrow layout where the panel cannot show (the desktop's narrow window, Gyro on the web and the Office pane;
 *    pictures 41, 42 and 43): tabs over the chat, "Chat | <short title>" ("Chat | Page" in the Office pane), and the page
 *    drawn `compact`.
 *  - the page alone in its own window: the desktop's pop-out (picture 13), the element's `starscribe-page` attribute.
 * The page and its header come from the editor, which loads the first time a page opens (lazy.ts).
 */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AutoSaveContext } from '../autoSaveContext.js';
import { isStarscribePageId } from '../client.js';
import { parseStarscribeAsk } from './pages.js';
import { ExternalLink, NotebookPen, X } from '../starscribeIcons.js';
import { STARSCRIBE_EDITOR_UNAVAILABLE, loadStarscribePage, loadedStarscribePage } from './lazy.js';
import { starscribeApiFor } from './transport.js';
/** The side panel tab's id; its payload is `{ pageId }`. */
export const STARSCRIBE_TAB_ID = 'starscribe';
/** The page a tab payload names, or null. */
export function payloadPageId(payload) {
    if (!payload || typeof payload !== 'object')
        return null;
    const pageId = payload.pageId;
    return isStarscribePageId(pageId) ? pageId.toLowerCase() : null;
}
/** What a tab without a page says (a person can pick the StarScribe tab while the panel shows Preview). */
export const NO_PAGE_OPEN = 'Open a page from StarScribe in the menu, or with Edit in StarScribe under an answer.';
/**
 * The editor's page, or its header, for one page: drawn once the editor has loaded, "Opening the page…" until then, and the
 * plain sentence when it did not load. The page's box carries `data-starscribe-page`, so Gyro's shortcuts leave its keys
 * alone (GyroAssistant.tsx: Ctrl+U underlines there, it does not attach a file).
 */
export function StarScribePageView({ part, ...given }) {
    // Beside a chat in the desktop app, an export is kept in Documents\Gyro by the chat's copies (autoSaveContext.ts).
    const autoSave = useContext(AutoSaveContext);
    const props = autoSave && !given.saveExport ? { ...given, saveExport: autoSave } : given;
    const [editor, setEditor] = useState(loadedStarscribePage);
    const [failed, setFailed] = useState('');
    useEffect(() => {
        if (editor)
            return undefined;
        let live = true;
        loadStarscribePage().then((loaded) => { if (live)
            setEditor(loaded); }, (failure) => { if (live)
            setFailed(failure instanceof Error && failure.message ? failure.message : STARSCRIBE_EDITOR_UNAVAILABLE); });
        return () => { live = false; };
    }, [editor]);
    if (part === 'header') {
        if (!editor)
            return _jsx("h2", { className: "gyro-side-panel-title", children: "StarScribe" });
        const Header = editor.StarScribePageHeader;
        return _jsx(Header, { ...props });
    }
    const Page = editor?.StarScribePage;
    return _jsx("div", { className: "gyro-ss-pagebox", "data-starscribe-page": "", children: Page ? _jsx(Page, { ...props })
            : failed ? _jsx("p", { className: "gyro-ss-status", role: "alert", children: failed })
                : _jsx("p", { className: "gyro-ss-status", role: "status", children: "Opening the page\u2026" }) });
}
/**
 * The desktop shell's page windows (WebAssets/starscribe-host.js): the widget asks with a cancelable
 * `gyro:starscribe-popout` `{ pageId, title }`, which the shell takes (preventDefault); the answer comes back as
 * `gyro:starscribe-popout-result` `{ pageId, status }`: opened, focused or refused. In a page's own window the shell sends
 * its asks as `gyro:starscribe-ask`, and the chat's window receives them as `gyro:starscribe-popout-ask`. The window closes
 * itself on `gyro:window-close`.
 */
export const STARSCRIBE_POPOUT_EVENT = 'gyro:starscribe-popout';
export const STARSCRIBE_POPOUT_RESULT_EVENT = 'gyro:starscribe-popout-result';
export const STARSCRIBE_ASK_EVENT = 'gyro:starscribe-ask';
export const STARSCRIBE_POPOUT_ASK_EVENT = 'gyro:starscribe-popout-ask';
export const WINDOW_CLOSE_EVENT = 'gyro:window-close';
/**
 * Whether the desktop shell is there to open page windows (its host script runs on the WebView2 bridge). The shell offers
 * Pop out in the desktop app only: Office on Windows hosts its task panes in WebView2 too (speechInput.ts).
 */
function desktopBridge() {
    if (typeof window === 'undefined')
        return false;
    const webview = window.chrome?.webview;
    return Boolean(webview && typeof webview.postMessage === 'function');
}
/**
 * Pop out (picture 11's ↗; picture 13): asks the desktop app to open the page in its own window. The shell answers with
 * `gyro:starscribe-popout-result`, which the shell hook reads (the page leaves the panel only once the window is there).
 */
export function StarScribePopOut({ pageId, title }) {
    if (!desktopBridge())
        return null;
    return _jsx("button", { type: "button", className: "gyro-btn ghost gyro-side-panel-button gyro-ss-popout", "aria-label": "Open the page in its own window", title: "Pop out", onClick: () => {
            window.dispatchEvent(new CustomEvent(STARSCRIBE_POPOUT_EVENT, { cancelable: true, detail: { pageId, title: title.trim() || 'StarScribe page' } }));
        }, children: _jsx(ExternalLink, { size: 16, "aria-hidden": "true" }) });
}
/**
 * The tab's header extras: the page's header, then Pop out (the desktop app's). Without a page, the tab's name.
 * `onWidth` hears the width the header has in the panel's head (it fills the room before Full screen and Close), so the
 * shell can draw the page compact when the panel is narrow.
 */
export function StarScribeTabHeader({ view, title, popOut, onWidth }) {
    const observer = useRef(null);
    const measure = useCallback((element) => {
        observer.current?.disconnect();
        observer.current = null;
        if (!element || !onWidth || typeof ResizeObserver !== 'function')
            return;
        const watch = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width ?? 0;
            if (width > 0)
                onWidth(Math.round(width));
        });
        watch.observe(element);
        observer.current = watch;
    }, [onWidth]);
    useEffect(() => () => observer.current?.disconnect(), []);
    if (!view)
        return _jsx("h2", { className: "gyro-side-panel-title", children: "StarScribe" });
    return _jsxs(_Fragment, { children: [_jsx("div", { className: "gyro-ss-head", ref: measure, children: _jsx(StarScribePageView, { part: "header", ...view }, `header:${view.pageId}`) }), popOut && _jsx(StarScribePopOut, { pageId: view.pageId, title: title })] });
}
/** The tab's body: the page, or the line that says how to open one. */
export function StarScribeTabBody({ view }) {
    if (!view)
        return _jsx("p", { className: "gyro-ss-status gyro-ss-tab-empty", children: NO_PAGE_OPEN });
    return _jsx(StarScribePageView, { part: "page", ...view }, `page:${view.pageId}`);
}
/**
 * The narrow layout's tabs over the chat (pictures 41 and 42): Chat, and the page by its short title ("Page" in the Office
 * pane, picture 43). After them, Pop out in the desktop app and Close, which closes the page and leaves the chat.
 */
export function StarScribeNarrowTabs({ label, title, view, onView, onClose, popOut }) {
    const keys = (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight' && event.key !== 'Home' && event.key !== 'End')
            return;
        event.preventDefault();
        const next = event.key === 'Home' ? 'chat' : event.key === 'End' ? 'page' : view === 'chat' ? 'page' : 'chat';
        onView(next);
        const tabs = event.currentTarget.querySelectorAll('[role="tab"]');
        tabs[next === 'chat' ? 0 : 1]?.focus();
    };
    return _jsxs("div", { className: "gyro-ss-strip", children: [_jsxs("div", { className: "gyro-ss-strip-tabs", role: "tablist", "aria-label": "Chat and page", onKeyDown: keys, children: [_jsx("button", { type: "button", role: "tab", className: "gyro-ss-strip-tab", "aria-selected": view === 'chat', tabIndex: view === 'chat' ? 0 : -1, onClick: () => onView('chat'), children: "Chat" }), _jsxs("button", { type: "button", role: "tab", className: "gyro-ss-strip-tab", "aria-selected": view === 'page', tabIndex: view === 'page' ? 0 : -1, onClick: () => onView('page'), title: title || undefined, children: [_jsx(NotebookPen, { size: 14, "aria-hidden": "true" }), _jsx("span", { children: label })] })] }), _jsxs("span", { className: "gyro-ss-strip-actions", children: [popOut, _jsx("button", { type: "button", className: "gyro-btn ghost gyro-side-panel-button gyro-ss-strip-close", "aria-label": "Close the page", title: "Close the page", onClick: onClose, children: _jsx(X, { size: 16, "aria-hidden": "true" }) })] })] });
}
/**
 * The narrow layout's page: its header and the page, both `compact`, in place of the chat. `notice` is said under the header
 * (the host's Open larger failed).
 */
export function StarScribeNarrowPage({ view, title, notice }) {
    return _jsxs("div", { className: "gyro-body gyro-ss-narrow", role: "tabpanel", "aria-label": title || 'StarScribe page', children: [_jsx("div", { className: "gyro-ss-narrow-head", children: _jsx(StarScribePageView, { part: "header", ...view, compact: true }, `header:${view.pageId}`) }), notice ? _jsx("p", { className: "gyro-ss-status gyro-ss-notice", role: "alert", children: notice }) : null, _jsx(StarScribePageView, { part: "page", ...view, compact: true }, `page:${view.pageId}`)] });
}
/**
 * An ask from the page in its own window, which has no chat of its own: told to the host as `gyro:starscribe-ask` on the
 * window (`detail` is the ask), for the desktop app to hand to the chat the page came from.
 */
function askFromWindow(input) {
    const ask = parseStarscribeAsk(input);
    if (ask)
        window.dispatchEvent(new CustomEvent(STARSCRIBE_ASK_EVENT, { cancelable: true, detail: ask }));
}
/** The page's own window closes (the desktop shell hides it): the page went away or closed itself. */
function closeWindow() {
    window.dispatchEvent(new CustomEvent(WINDOW_CLOSE_EVENT, { cancelable: true }));
}
/**
 * The page alone, filling its window, with no chat, menu or panel around it: the desktop's pop-out (picture 13; the
 * element's `starscribe-page="<pageId>"`) and the Office pane's Open larger dialog (picture 43; exported from
 * `@exante/gyro-react`). Its asks go out as the window event `gyro:starscribe-ask` and it closes with `gyro:window-close`,
 * for the window's host to hand on to the chat the page came from (as `gyro:starscribe-popout-ask` there).
 */
export function StarScribePageWindow({ client, pageId, theme, you, rates, saveExport }) {
    const valid = isStarscribePageId(pageId);
    const view = { api: starscribeApiFor(client), pageId: pageId.toLowerCase(), ask: askFromWindow, theme, you: you ?? { name: '', email: '' }, onClose: closeWindow, ...(rates ? { rates } : {}), ...(saveExport ? { saveExport } : {}) };
    return _jsx("div", { className: "gyro-panel gyro-pane gyro-desktop-workspace gyro-ss-window", "data-theme": theme, children: valid ? _jsxs(_Fragment, { children: [_jsx("div", { className: "gyro-ss-window-head", children: _jsx(StarScribePageView, { part: "header", ...view }) }), _jsx(StarScribePageView, { part: "page", ...view })] }) : _jsx("p", { className: "gyro-ss-status", role: "alert", children: "This page is no longer available to you." }) });
}
//# sourceMappingURL=tab.js.map